/**
 * BaseRow Component
 *
 * Standard layout pattern for ALL entity rows.
 * Provides consistent structure with customization slots.
 *
 * Layout:
 * ┌─────────────────────────────────────────────────────┐
 * │ [Icon/Avatar] [Title]              [Fast Actions]  │
 * │               [Meta/Subtitle]                       │
 * └─────────────────────────────────────────────────────┘
 */

import React, { useState, useMemo } from 'react';
import type { BaseRowProps, ActionType } from '../../../types/row.types';

// =============================================================================
// STYLES
// =============================================================================

const styles = {
  row: {
    display: 'flex',
    alignItems: 'stretch',
    minHeight: '36px',
    borderBottom: '1px solid #e8e8e8',
    background: '#fff',
    fontSize: '13px',
    transition: 'background 0.1s',
  } as React.CSSProperties,
  rowDone: {
    background: '#fafafa',
  } as React.CSSProperties,
  rowHover: {
    background: '#f5f7fa',
  } as React.CSSProperties,
  left: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flex: 1,
    minWidth: 0,
    padding: '4px 12px',
  } as React.CSSProperties,
  right: {
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    padding: '4px 8px',
    borderLeft: '1px solid #f0f0f0',
  } as React.CSSProperties,
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
  } as React.CSSProperties,
  avatarsStack: {
    display: 'flex',
    marginRight: '4px',
    flexShrink: 0,
  } as React.CSSProperties,
  avatar: {
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    border: '1.5px solid #fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '9px',
    fontWeight: 600,
    color: '#fff',
  } as React.CSSProperties,
  contentArea: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  } as React.CSSProperties,
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
  } as React.CSSProperties,
  title: {
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  } as React.CSSProperties,
  titleDone: {
    textDecoration: 'line-through',
    color: '#999',
  } as React.CSSProperties,
  metaContent: {
    fontSize: '12px',
    color: '#666',
  } as React.CSSProperties,
  actionBtn: {
    background: 'none',
    border: 'none',
    padding: '4px',
    cursor: 'pointer',
    display: 'flex',
    borderRadius: '4px',
    transition: 'all 0.1s',
  } as React.CSSProperties,
  actionBtnHover: {
    background: '#e5e5e5',
  } as React.CSSProperties,
};

// =============================================================================
// COMPONENT
// =============================================================================

export function BaseRow<T = any>({
  entity,
  entityType,
  leftSlot,
  icon,
  avatars = [],
  metaContent,
  rightContent,
  availableActions = [],
  linkedActionCounts = {},
  onTitleClick,
  onActionClick,
  variant = 'default',
  done = false,
  className = '',
}: BaseRowProps<T>) {
  const [hovered, setHovered] = useState(false);

  // Get title from entity (assumes entity has 'title' or 'name' property)
  const title = useMemo(() => {
    const e = entity as any;
    return e.title || e.name || e.displayName || 'Untitled';
  }, [entity]);

  // Get entity ID (assumes entity has 'id' property)
  const entityId = useMemo(() => {
    const e = entity as any;
    return e.id || '';
  }, [entity]);

  const handleTitleClick = () => {
    if (onTitleClick) {
      onTitleClick(entity);
    }
  };

  const handleActionClick = (actionType: ActionType) => {
    if (onActionClick && entityId) {
      onActionClick(actionType, entityId);
    }
  };

  return (
    <div
      className={`base-row ${className}`}
      style={{
        ...styles.row,
        ...(done ? styles.rowDone : {}),
        ...(hovered && !done ? styles.rowHover : {}),
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* LEFT ZONE */}
      <div style={styles.left}>
        {/* Primary action slot (check, eye, archive, star) */}
        {leftSlot && (
          <div
            style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}
            onClick={(e) => e.stopPropagation()}
          >
            {leftSlot}
          </div>
        )}

        {/* Custom icon slot */}
        {icon && <div style={styles.iconContainer}>{icon}</div>}

        {/* Avatars stack */}
        {avatars.length > 0 && <AvatarsStack assignees={avatars} />}

        {/* Content area (title + meta) */}
        <div style={styles.contentArea}>
          {/* Title row */}
          <div style={styles.titleRow} onClick={handleTitleClick}>
            <span style={{ ...styles.title, ...(done ? styles.titleDone : {}) }}>
              {title}
            </span>
          </div>

          {/* Meta content */}
          {metaContent && <div style={styles.metaContent}>{metaContent}</div>}
        </div>
      </div>

      {/* RIGHT ZONE */}
      <div style={{ ...styles.right, opacity: hovered ? 1 : 0.4 }}>
        {/* Additional right content (e.g., timer) */}
        {rightContent}

        {/* Fast actions */}
        {availableActions.map((actionType) => {
          const count = linkedActionCounts[actionType] || 0;
          const isLinked = count > 0;

          return (
            <FastActionButton
              key={actionType}
              actionType={actionType}
              isLinked={isLinked}
              count={count}
              onClick={() => handleActionClick(actionType)}
            />
          );
        })}
      </div>
    </div>
  );
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

/**
 * Avatars stack component
 * Shows up to 3 avatars with overflow indicator
 */
function AvatarsStack({ assignees }: { assignees: string[] }) {
  if (assignees.length === 0) {
    return null;
  }

  const visible = assignees.slice(0, 3);
  const extra = assignees.length - 3;

  return (
    <div style={styles.avatarsStack}>
      {visible.map((userId, i) => (
        <div
          key={userId}
          style={{
            ...styles.avatar,
            background: `hsl(${(userId.charCodeAt(4) * 37) % 360}, 55%, 55%)`,
            marginLeft: i > 0 ? '-6px' : '0',
            zIndex: 3 - i,
          }}
          title={userId}
        >
          {userId.slice(-2).toUpperCase()}
        </div>
      ))}
      {extra > 0 && (
        <div
          style={{
            ...styles.avatar,
            background: '#999',
            marginLeft: '-6px',
          }}
        >
          +{extra}
        </div>
      )}
    </div>
  );
}

/**
 * Fast action button component with counter badge
 * Shows count of linked items above icon when count > 0
 */
function FastActionButton({
  actionType,
  isLinked,
  count,
  onClick,
}: {
  actionType: ActionType;
  isLinked: boolean;
  count: number;
  onClick: () => void;
}) {
  const [hover, setHover] = useState(false);

  // SVG icon paths for each action type
  const getIconPath = (type: ActionType): string => {
    const paths: Record<ActionType, string> = {
      task: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
      event: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
      email: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
      call: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z',
      message: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
      reminder: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9',
      note: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
      videocall: 'M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z',
      file: 'M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13',
      invoice: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
    };
    return paths[type] || '';
  };

  // Label for each action type (Italian)
  const getLabel = (type: ActionType): string => {
    const labels: Record<ActionType, string> = {
      task: 'Task',
      event: 'Events',
      email: 'Email',
      call: 'Calls',
      message: 'Messages',
      reminder: 'Reminders',
      note: 'Notes',
      videocall: 'Video calls',
      file: 'Files',
      invoice: 'Invoices',
    };
    return labels[type] || type;
  };

  // Color based on link status
  const baseColor = isLinked ? '#3b82f6' : '#9ca3af';
  const hoverColor = isLinked ? '#2563eb' : '#6b7280';

  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      style={{
        ...styles.actionBtn,
        position: 'relative',
        color: hover ? hoverColor : baseColor,
        ...(hover ? styles.actionBtnHover : {}),
      }}
      title={`${getLabel(actionType)}${count > 0 ? ` (${count})` : ''}`}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {/* Counter badge */}
      {count > 0 && (
        <span
          style={{
            position: 'absolute',
            top: '-4px',
            right: '-4px',
            minWidth: '14px',
            height: '14px',
            padding: '0 3px',
            borderRadius: '7px',
            background: '#3b82f6',
            color: '#fff',
            fontSize: '9px',
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 1,
            boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
          }}
        >
          {count > 99 ? '99+' : count}
        </span>
      )}

      {/* SVG Icon */}
      <svg
        width="14"
        height="14"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        style={{ display: 'block' }}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d={getIconPath(actionType)}
        />
      </svg>
    </button>
  );
}

// Export FastActionButton for use in other components
export { FastActionButton };
